import React, { useState, useEffect } from "react";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import {
  Card,
  CardBody,
  Col,
  Container,
  CardHeader,
  Nav,
  NavItem,
  NavLink,
  Row,
  TabContent,
  TabPane,
  Input,
  Label,
  FormFeedback,
  Form,
  Spinner,
  Alert,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Table,
} from "reactstrap";

// Redux
import { useDispatch } from "react-redux";

import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import classnames from "classnames";
import Dropzone from "react-dropzone";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";

// Toast
import { toast, ToastContainer } from "react-toastify";

//formik
import { useFormik } from "formik";
import * as Yup from "yup";

// Import React FilePond
import { registerPlugin } from "react-filepond";
import Flatpickr from "react-flatpickr";
import Select from "react-select";
// Import FilePond styles
import "filepond/dist/filepond.min.css";
import FilePondPluginImageExifOrientation from "filepond-plugin-image-exif-orientation";
import FilePondPluginImagePreview from "filepond-plugin-image-preview";
import "filepond-plugin-image-preview/dist/filepond-plugin-image-preview.css";

// RTK Query hooks
import {
  useGetCategoriesQuery,
  useGetMeasurementsQuery,
  useGetTagsQuery,
  useCreateProductMutation,
  useUpdateProductMutation,
  useGetProductByIdQuery,
  useCreateProductAttachmentMutation,
  useGetProductsSupplierByIdQuery,
  useGetSupplierforProductByIdQuery,
  useCreateSupplierCostMappingMutation,
  useUpdateSupplierCostMappingMutation,
  useGetSuppliersQuery,
  useDeleteSupplierCostMappingByIdMutation,
  useGetInventoryImageAttachmentsQuery,
  useRemoveProductImageAssignmentMutation,
} from "slices/InventoryManagement/inventoryManagementSlice";
import { useUpdateInventoryImageAttachmentMutation } from "slices/InventoryManagement/inventoryManagementSlice";
import ProductImageSelectionModal from "../CommonComponents/ProductImageSelectionModal";
import AddSupplierModal from "./AddSupplier";
import ProductDocumentChecklistSection from "./ProductDocumentChecklistSection";
import PriceViewComponent, {
  PriceViewCompact,
} from "../CommonComponents/PriceViewComponent";
import { any, number } from "prop-types";

// Register the plugins
registerPlugin(FilePondPluginImageExifOrientation, FilePondPluginImagePreview);

const DEFAULT_ATTRIBUTE_PREVIEW_LIMIT = 4;

// Define types for our API responses
interface Category {
  id: number;
  name: string;
  description: string;
}

interface Measurement {
  id: number;
  name: string;
}

interface Tag {
  id: number;
  name: string;
  description: string;
}

interface Attachment {
  id?: number;
  name: string;
  description: string;
  file: string;
  file_url?: string;
  product?: number;
}

interface Product {
  id?: number;
  name: string;
  description: string;
  item_code: string;
  item_group_code: string;
  brand_company: string;
  model_number: string;
  size: string;
  weight: string;
  dimensions: string;
  status: number;
  category: number;
  supplier: number | null;
  measurement: number;
  tags: number[];
  attachment?: string;
  thumbnail?: string;
  attachments?: Attachment[];
  attachment_urls?: string[];
}

interface SupplierCostMapping {
  mapping_id: number;
  supplier_id: number;
  supplier_name: string;
  supplier_email: string | null;
  supplier_phone: string | null;
  total_amount: number;
  discount: number;
  brand_name: string | null;
  model_number: string | null;
}

interface BulkSupplierMapping {
  supplier: number[];
  product: number;
  total_amount: number;
  discount: number;
}

// Supplier Row Component for inline editing
interface SupplierRowProps {
  supplier: SupplierCostMapping;
  onUpdate: (data: {
    supplier: number;
    product: number;
    total_amount: string;
    discount: string;
    brand_name: string;
    model_number: string;
  }) => Promise<void>;
  onDelete: (mappingId: number) => Promise<void>;
  isLoading: boolean;
  isDeleting: boolean;
  productId: number;
  readOnly?: boolean;
}

const SupplierRow: React.FC<SupplierRowProps> = ({
  supplier,
  onUpdate,
  onDelete,
  isLoading,
  isDeleting,
  productId,
  readOnly = false,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({
    total_amount: supplier.total_amount.toString(),
    discount: supplier.discount.toString(),
    brand_name: supplier.brand_name || "",
    model_number: supplier.model_number || "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deleteConfirmModal, setDeleteConfirmModal] = useState(false);

  const handleEdit = () => {
    setIsEditing(true);
    setEditData({
      total_amount: supplier.total_amount.toString(),
      discount: supplier.discount.toString(),
      brand_name: supplier.brand_name || "",
      model_number: supplier.model_number || "",
    });
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditData({
      total_amount: supplier.total_amount.toString(),
      discount: supplier.discount.toString(),
      brand_name: supplier.brand_name || "",
      model_number: supplier.model_number || "",
    });
  };

  const handleSave = async () => {
    try {
      setIsSubmitting(true);
      await onUpdate({
        supplier: supplier.supplier_id,
        product: productId,
        total_amount: editData.total_amount,
        discount: editData.discount,
        brand_name: editData.brand_name,
        model_number: editData.model_number,
      });
      setIsEditing(false);
    } catch (error) {
      console.error("Failed to update supplier:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    try {
      await onDelete(supplier.mapping_id);
      setDeleteConfirmModal(false);
    } catch (error) {
      console.error("Failed to delete supplier:", error);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setEditData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const isSaveDisabled =
    isSubmitting ||
    !editData.total_amount ||
    isNaN(Number(editData.total_amount)) ||
    Number(editData.total_amount) < 0 ||
    isNaN(Number(editData.discount)) ||
    Number(editData.discount) < 0 ||
    Number(editData.discount) > 100;

  return (
    <>
      <tr>
        <td>
          <strong>{supplier.supplier_name}</strong>
        </td>
        <td>{supplier.supplier_email || "-"}</td>
        <td>{supplier.supplier_phone || "-"}</td>
        <td>
          {isEditing ? (
            <Input
              type="text"
              className="form-control form-control-sm"
              value={editData.brand_name}
              onChange={(e) => handleInputChange("brand_name", e.target.value)}
              disabled={isSubmitting}
              placeholder="Brand name"
              style={{ minWidth: "130px" }}
            />
          ) : (
            supplier.brand_name || "-"
          )}
        </td>
        <td>
          {isEditing ? (
            <Input
              type="text"
              className="form-control form-control-sm"
              value={editData.model_number}
              onChange={(e) =>
                handleInputChange("model_number", e.target.value)
              }
              disabled={isSubmitting}
              placeholder="Model number"
              style={{ minWidth: "130px" }}
            />
          ) : (
            supplier.model_number || "-"
          )}
        </td>
        <td className="text-end">
          {isEditing ? (
            <div className="d-flex align-items-center justify-content-end">
              <span className="me-2">$</span>
              <Input
                type="number"
                className="form-control form-control-sm text-end"
                value={editData.total_amount}
                onChange={(e) =>
                  handleInputChange("total_amount", e.target.value)
                }
                disabled={isSubmitting}
                style={{ width: "120px" }}
                min="0"
                step="0.01"
              />
            </div>
          ) : (
            <PriceViewCompact price={supplier.total_amount} />
          )}
        </td>
        <td className="text-end">
          {isEditing ? (
            <div className="d-flex align-items-center justify-content-end">
              <Input
                type="number"
                className="form-control form-control-sm text-end"
                value={editData.discount}
                onChange={(e) => handleInputChange("discount", e.target.value)}
                disabled={isSubmitting}
                style={{ width: "80px" }}
                min="0"
                max="100"
                step="0.1"
              />
              <span className="ms-1">%</span>
            </div>
          ) : (
            `${supplier.discount}%`
          )}
        </td>
        {!readOnly && <td>
          <div className="d-flex gap-2">
            {isEditing ? (
              <>
                <Button
                  color="success"
                  size="sm"
                  onClick={handleSave}
                  disabled={isSaveDisabled}
                >
                  {isSubmitting ? (
                    <Spinner size="sm" />
                  ) : (
                    <i className="ri-check-line"></i>
                  )}
                </Button>
                <Button
                  color="secondary"
                  size="sm"
                  onClick={handleCancel}
                  disabled={isSubmitting}
                >
                  <i className="ri-close-line"></i>
                </Button>
              </>
            ) : (
              <>
                <Button
                  color="primary"
                  size="sm"
                  onClick={handleEdit}
                  disabled={isLoading || isDeleting}
                >
                  <i className="ri-edit-line"></i>
                </Button>
                <Button
                  color="danger"
                  size="sm"
                  onClick={() => setDeleteConfirmModal(true)}
                  disabled={isLoading || isDeleting}
                >
                  {isDeleting ? (
                    <Spinner size="sm" />
                  ) : (
                    <i className="ri-delete-bin-line"></i>
                  )}
                </Button>
              </>
            )}
          </div>
        </td>}
      </tr>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={deleteConfirmModal}
        toggle={() => setDeleteConfirmModal(false)}
      >
        <ModalHeader toggle={() => setDeleteConfirmModal(false)}>
          Confirm Delete
        </ModalHeader>
        <ModalBody>
          Are you sure you want to remove{" "}
          <strong>{supplier.supplier_name}</strong> from this product? This
          action cannot be undone.
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            onClick={() => setDeleteConfirmModal(false)}
          >
            Cancel
          </Button>
          <Button color="danger" onClick={handleDelete} disabled={isDeleting}>
            {isDeleting ? (
              <>
                <Spinner size="sm" className="me-2" />
                Deleting...
              </>
            ) : (
              "Delete Supplier"
            )}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};

const EcommerceAddProduct = () => {
  document.title = "Create Inventory Product";

  const { Id } = useParams<{ Id: string }>();
  const [searchParams] = useSearchParams();
  const isEditMode = Boolean(Id);
  const isViewMode = isEditMode && searchParams.get("mode") === "view";
  const history = useNavigate();

  // API hooks
  const { data: categoriesData, isLoading: categoriesLoading } =
    useGetCategoriesQuery(undefined);
  const { data: measurementsData, isLoading: measurementsLoading } =
    useGetMeasurementsQuery(undefined);
  const { data: tagsData, isLoading: tagsLoading } = useGetTagsQuery(undefined);
  const {
    data: productData,
    isLoading: productLoading,
    refetch: refetchProduct,
  } = useGetProductByIdQuery(Id as string, { skip: !Id });

  // Add pagination state with search
  const [page, setPageState] = useState<{
    page: number;
    pageSize: number;
    search: string;
  }>({
    page: 1,
    pageSize: 12,
    search: "",
  });

  // Add these states
  const [imageSelectionModal, setImageSelectionModal] = useState(false);
  const [selectedExistingImage, setSelectedExistingImage] = useState<{
    id: number;
    name: string;
    file_url: string;
    file: string;
  } | null>(null);
  
  const [deleteConfirmModal, setDeleteConfirmModal] = useState<{
    isOpen: boolean;
    attachmentId: number | null;
    attachmentName: string;
  }>({
    isOpen: false,
    attachmentId: null,
    attachmentName: "",
  });

  // Add confirmation modal state for image selection
  const [imageConfirmModal, setImageConfirmModal] = useState<{
    isOpen: boolean;
    image: {
      id: number;
      name: string;
      file_url: string;
      file: string;
      products?: number[];
    } | null;
  }>({
    isOpen: false,
    image: null,
  });

  const {
    data: productImageData,
    isLoading: productImageLoading,
    refetch: refetchImageProduct,
  } = useGetInventoryImageAttachmentsQuery(page);

  console.log('productImageData', productImageData);

  const [createProduct, { isLoading: creating, error: createError }] =
    useCreateProductMutation();
  const [updateProduct, { isLoading: updating, error: updateError }] =
    useUpdateProductMutation();

  // Attachment APIs
  const [createProductAttachment, { isLoading: creatingAttachment }] =
    useCreateProductAttachmentMutation();
  // Supplier API hooks
  const { data: productSuppliers, refetch: refetchProductSuppliers } =
    useGetSupplierforProductByIdQuery(Id as string, { skip: !Id });

  const [
    createSupplierCostMapping,
    { isLoading: creatingSupplierMapping, error: createSupplierError },
  ] = useCreateSupplierCostMappingMutation();

  const [
    updateSupplierCostMapping,
    { isLoading: updatingSupplierMapping, error: updateSupplierError },
  ] = useUpdateSupplierCostMappingMutation();

  const [
    deleteSupplierCostMappingById,
    { isLoading: deletingSupplierMapping },
  ] = useDeleteSupplierCostMappingByIdMutation();

  // Update Image Attachment Mutation
  const [updateInventoryImageAttachment, { isLoading: updatingImage }] =
    useUpdateInventoryImageAttachmentMutation();
  const [removeProductImageAssignment] = useRemoveProductImageAssignmentMutation();

  const [customActiveTab, setcustomActiveTab] = useState<any>("1");
  const [selectedFiles, setselectedFiles] = useState<any>([]);
  const [selectedVisibility, setselectedVisibility] = useState<any>(null);
  const [selectedImage, setSelectedImage] = useState<any>();
  const [selectedTags, setSelectedTags] = useState<any[]>([]);
  const [editorData, setEditorData] = useState<string>("");
  const [existingAttachments, setExistingAttachments] = useState<Attachment[]>(
    []
  );
  const [filesToRemove, setFilesToRemove] = useState<number[]>([]);
  const [deletingAttachmentId, setDeletingAttachmentId] = useState<
    number | null
  >(null);

  // Modal states
  const [attachmentModal, setAttachmentModal] = useState(false);
  const [imagePreviewModal, setImagePreviewModal] = useState(false);
  const [previewImage, setPreviewImage] = useState("");
  const [newAttachment, setNewAttachment] = useState({
    name: "",
    description: "",
    file: null as File | null,
  });

  // Supplier Management States
  const [supplierModal, setSupplierModal] = useState(false);

  // Product Attributes (key/value pairs stored in the product's attributes JSON)
  const [productAttributes, setProductAttributes] = useState<
    { key: string; value: string }[]
  >([]);
  const [showAllAttributes, setShowAllAttributes] = useState(false);
  const [attributePreviewLimit, setAttributePreviewLimit] = useState(
    DEFAULT_ATTRIBUTE_PREVIEW_LIMIT
  );

  // Format data for Select components
  const categoryOptions =
    categoriesData?.results?.map((category: Category) => ({
      value: category.id,
      label: category.name,
    })) || [];

  const measurementOptions =
    measurementsData?.results?.map((measurement: Measurement) => ({
      value: measurement.id,
      label: measurement.name,
    })) || [];

  const tagOptions =
    tagsData?.results?.map((tag: Tag) => ({
      value: tag.id,
      label: tag.name,
    })) || [];

  const statusOptions = [
    { value: 1, label: "Draft" },
    { value: 2, label: "Publish" },
    { value: 3, label: "Disable" },
  ];

  const visibleProductAttributes = showAllAttributes
    ? productAttributes
    : productAttributes.slice(0, attributePreviewLimit);
  const isShowingAllAttributes =
    showAllAttributes || attributePreviewLimit >= productAttributes.length;

  const toggleAttributePreview = () => {
    if (isShowingAllAttributes) {
      setShowAllAttributes(false);
      setAttributePreviewLimit(
        Math.min(DEFAULT_ATTRIBUTE_PREVIEW_LIMIT, productAttributes.length)
      );
      return;
    }

    setShowAllAttributes(true);
  };

  // Handle image selection from library
  const handleSelectImageFromLibrary = (image: {
    id: number;
    name: string;
    file_url: string;
    file: string;
    products?: number[];
  }) => {
    setImageConfirmModal({
      isOpen: true,
      image: image,
    });
    setImageSelectionModal(false);
  };

  // Handle confirmation for image selection
  const handleConfirmImageSelection = async () => {
    if (!imageConfirmModal.image || !Id) return;
    
    try {
      const image = imageConfirmModal.image;
      const productId = parseInt(Id);

      // Check if image is already attached
      const isAlreadyAttached = image.products?.includes(productId);
      if (isAlreadyAttached) {
        toast.warning("This image is already attached to this product");
        setImageConfirmModal({ isOpen: false, image: null });
        return;
      }

      // Prepare the updated products array
      const currentProducts = image.products || [];
      const updatedProducts = [...currentProducts, productId];

      // Create the update data for PATCH
      const updateData = {
        products: updatedProducts
      };

      // Use updateInventoryImageAttachment mutation (PATCH)
      await updateInventoryImageAttachment({
        id: image.id,
        values: updateData
      }).unwrap();
      
      toast.success("Image attached to product successfully");
      refetchProduct(); // Refresh product data
      refetchImageProduct(); // Refresh image list
      
      // Set the thumbnail image for preview
      setSelectedImage(image.file_url);
      setImageConfirmModal({ isOpen: false, image: null });
    } catch (error: any) {
      console.error("Failed to attach image:", error);
      toast.error(error?.data?.message || "Failed to attach image to product");
      setImageConfirmModal({ isOpen: false, image: null });
    }
  };

  // Handle search change
  const handleSearchChange = (search: string) => {
    console.log('Setting search term:', search);
    setPageState(prev => ({
      ...prev,
      search: search.trim(),
      page: 1,
    }));
  };

  // Handle page change
  const handlePageChange = (newPage: number) => {
    console.log('Page changing to:', newPage);
    setPageState(prev => ({ ...prev, page: newPage }));
  };

  // Handle clear search
  const handleClearSearch = () => {
    setPageState({
      page: 1,
      pageSize: 12,
      search: "",
    });
  };

  // Reset page state when modal opens
  useEffect(() => {
    if (imageSelectionModal) {
      setPageState({
        page: 1,
        pageSize: 12,
        search: "",
      });
    }
  }, [imageSelectionModal]);

  // Set form values when in edit mode and product data is available
  useEffect(() => {
    if (isEditMode && productData) {
      // Set form values from productData
      validation.setValues({
        name: productData.name || "",
        description: productData.description || "",
        item_code: productData.item_code || "",
        item_group_code: productData.item_group_code || "",
        brand_company: productData.brand_company || "",
        model_number: productData.model_number || "",
        size: productData.size || "",
        weight: productData.weight || "",
        dimensions: productData.dimensions || "",
        status: productData.status || 1,
        category: productData.category || "",
        supplier: productData.supplier || null,
        measurement: productData.measurement || "",
        tags: productData.tags || [],
      });

      setEditorData(productData.description || "");

      // Load product attributes (JSON object) into the key/value editor
      if (
        productData.attributes &&
        typeof productData.attributes === "object" &&
        !Array.isArray(productData.attributes)
      ) {
        setProductAttributes(
          Object.entries(productData.attributes).map(([key, value]) => ({
            key,
            value: String(value ?? ""),
          }))
        );
      }

      // Set selected tags for multi-select
      if (productData.tags && productData.tags.length > 0) {
        const tagsSelection = productData.tags
          .map((tagId: any) => {
            const tag = tagsData?.results?.find(
              (t: { id: any }) => t.id === tagId
            );
            return tag ? { value: tag.id, label: tag.name } : null;
          })
          .filter(Boolean);
        setSelectedTags(tagsSelection);
      }

      // Set image if available
      if (productData.thumbnail) {
        setSelectedImage(productData.thumbnail_url);
      }

      // Set existing attachments if available
      if (productData.attachments && productData.attachments.length > 0) {
        setExistingAttachments(productData.attachments);
      }
    }
  }, [isEditMode, productData, tagsData]);

  const toggleCustom = (tab: any) => {
    if (customActiveTab !== tab) {
      setcustomActiveTab(tab);
    }
  };

  function handleAcceptedFiles(files: any) {
    const newFiles = files.map((file: any) =>
      Object.assign(file, {
        preview: URL.createObjectURL(file),
        formattedSize: formatBytes(file.size),
      })
    );
    setselectedFiles([...selectedFiles, ...newFiles]);
  }

  function handleRemoveFile(index: number) {
    const newFiles = [...selectedFiles];
    newFiles.splice(index, 1);
    setselectedFiles(newFiles);
  }

  // Update the handleRemoveExistingFile function
  const handleRemoveExistingFile = async (id: number) => {
    if (!Id) return;
    try {
      setDeletingAttachmentId(id);
      await removeProductImageAssignment({ imageId: id, productId: parseInt(Id, 10) }).unwrap();
      toast.success("Image removed from this product. It remains in the image library.");
      refetchProduct();
      refetchImageProduct();
      setDeleteConfirmModal({
        isOpen: false,
        attachmentId: null,
        attachmentName: "",
      });
    } catch (error: any) {
      console.error("Failed to remove product image:", error);
      toast.error(error?.data?.error || "Failed to remove image from this product.");
    } finally {
      setDeletingAttachmentId(null);
    }
  };

  // Add a function to open delete confirmation modal
  const openDeleteConfirmModal = (attachmentId: number, attachmentName: string) => {
    setDeleteConfirmModal({
      isOpen: true,
      attachmentId,
      attachmentName,
    });
  };

  function handleSelectVisibility(selectedVisibility: any) {
    setselectedVisibility(selectedVisibility);
  }

  function formatBytes(bytes: any, decimals = 2) {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];

    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
  }

  const handleTagsChange = (selectedOptions: any) => {
    setSelectedTags(selectedOptions || []);
    validation.setFieldValue(
      "tags",
      selectedOptions ? selectedOptions.map((option: any) => option.value) : []
    );
  };

  // Handle adding new attachment
  const handleAddAttachment = async () => {
    if (!newAttachment.file || !Id) return;

    try {
      const formData = new FormData();
      formData.append("name", newAttachment.name);
      formData.append("description", newAttachment.description);
      formData.append("product", Id);
      formData.append("file", newAttachment.file);

      await createProductAttachment(formData).unwrap();

      // Reset form and close modal
      setNewAttachment({
        name: "",
        description: "",
        file: null,
      });
      setAttachmentModal(false);

      // Refresh the product data to get updated attachments
      refetchProduct();
      toast.success("Attachment added successfully");
    } catch (error) {
      console.error("Failed to add attachment:", error);
      toast.error("Failed to add attachment");
    }
  };

  const handleDeleteSupplier = async (mappingId: number) => {
    try {
      await deleteSupplierCostMappingById(mappingId).unwrap();
      toast.success("Supplier removed successfully");
      refetchProductSuppliers();
    } catch (error: any) {
      console.error("Failed to delete supplier:", error);
      toast.error(error?.data?.message || "Failed to delete supplier");
      throw error;
    }
  };

  // Handle image preview
  const handleImagePreview = (imageUrl: string) => {
    setPreviewImage(imageUrl);
    setImagePreviewModal(true);
  };

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: "",
      description: "",
      item_code: "",
      item_group_code: "",
      brand_company: "",
      model_number: "",
      size: "",
      weight: "",
      dimensions: "",
      status: 1,
      category: "",
      supplier: "",
      measurement: "",
      tags: [],
    },
    validationSchema: Yup.object({
      name: Yup.string().required("Please Enter an Item Name"),
      description: Yup.string()
        .required("Please Enter a Product Description")
        .max(500, "Product Description cannot exceed 500 characters"),
      item_code: Yup.string(),
      item_group_code: Yup.string(),
      brand_company: Yup.string().required("Please Enter a Brand Name"),
      model_number: Yup.string(),
      status: Yup.number().required("Please Select a Status"),
      category: Yup.number().required("Please Select a Category"),
      measurement: Yup.number().required("Please Select a Measurement"),
    }),
    onSubmit: async (values) => {
      try {
        const formData = new FormData();
        formData.append("name", values.name);
        formData.append("description", values.description);
        formData.append("item_code", values.item_code);
        formData.append("item_group_code", values.item_group_code);
        formData.append("brand_company", values.brand_company);
        formData.append("model_number", values.model_number);

        // Product attributes: key/value rows -> JSON object
        const attributesObj: Record<string, string> = {};
        productAttributes.forEach((attr) => {
          if (attr.key.trim()) {
            attributesObj[attr.key.trim()] = attr.value.trim();
          }
        });
        formData.append("attributes", JSON.stringify(attributesObj));
        formData.append("size", values.size || "");
        formData.append("weight", values.weight || "");
        formData.append("dimensions", values.dimensions || "");
        formData.append("status", String(values.status));
        formData.append("category", String(values.category));
        if (values.supplier)
          formData.append("supplier", String(values.supplier));
        formData.append("measurement", String(values.measurement));

        values.tags.forEach((tag: number) =>
          formData.append("tags", String(tag))
        );

        if (selectedImage instanceof File) {
          formData.append("thumbnail", selectedImage);
        }

        // Append new attachments
        selectedFiles.forEach((file: File) => {
          formData.append("attachments", file);
        });

        // Append files to remove (for edit mode)
        if (isEditMode) {
          filesToRemove.forEach((id: number) => {
            formData.append("remove_attachments", String(id));
          });
        }

        let result;
        if (isEditMode && Id) {
          result = await updateProduct({
            id: parseInt(Id),
            productData: formData,
          }).unwrap();
          // Don't redirect, just refetch the product data
          refetchProduct();
          toast.success("Product updated successfully");
        } else {
          result = await createProduct(formData).unwrap();
          toast.success("Product created successfully");
          // For new products, redirect to edit page with the new ID
          history(`/add-inventory-product/${result.id}`);
        }
      } catch (error: any) {
        console.error("Failed to save product:", error);
        toast.error(error?.data?.message || "Failed to save product");
      }
    },
  });

  // Add suppliers in bulk
  const handleAddSupplier = async (supplierData: BulkSupplierMapping) => {
    try {
      await createSupplierCostMapping(supplierData).unwrap();
      toast.success("Suppliers added successfully");

      // Refresh the suppliers list
      refetchProductSuppliers();
      setSupplierModal(false);
    } catch (error: any) {
      console.error("Failed to save supplier mapping:", error);
      toast.error(error?.data?.message || "Failed to save supplier mapping");
      throw error;
    }
  };

  // Update individual supplier mapping
  const handleUpdateSupplier = async (supplierData: {
    supplier: number;
    product: number;
    total_amount: string;
    discount: string;
    brand_name: string;
    model_number: string;
  }) => {
    try {
      // Find the mapping_id from the existing suppliers
      const existingSupplier = productSuppliers?.find(
        (s: SupplierCostMapping) => s.supplier_id === supplierData.supplier
      );

      if (existingSupplier) {
        await updateSupplierCostMapping({
          id: existingSupplier.mapping_id,
          values: supplierData,
        }).unwrap();
        toast.success("Supplier updated successfully");
        refetchProductSuppliers();
      }
    } catch (error: any) {
      console.error("Failed to update supplier:", error);
      toast.error(error?.data?.message || "Failed to update supplier");
      throw error;
    }
  };

  if (
    (isEditMode && productLoading) ||
    categoriesLoading ||
    measurementsLoading ||
    tagsLoading
  ) {
    return (
      <div className="page-content">
        <Container fluid>
          <div className="text-center">
            <Spinner color="primary" />
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title={isViewMode ? "View Product" : isEditMode ? "Edit Product" : "Create Product"}
          pageTitle="Ecommerce"
        />

        {(createError || updateError) && (
          <Alert color="danger">
            {((createError || updateError) as any)?.data?.message ||
              "An error occurred while saving the product"}
          </Alert>
        )}

        <Row>
          <Col lg={8}>
            <Form
              onSubmit={(e) => {
                e.preventDefault();
                if (!isViewMode) validation.handleSubmit();
                return false;
              }}
            >
              <Card>
                <CardBody>
                  <div className="mb-3">
                    <Label className="form-label" htmlFor="product-name-input">
                      Item Name <span className="text-danger">*</span>
                    </Label>
                    <Input
                      type="text"
                      className="form-control"
                      id="product-name-input"
                      placeholder="Enter item name"
                      name="name"
                      value={validation.values.name || ""}
                      onBlur={validation.handleBlur}
                      onChange={validation.handleChange}
                      disabled={isViewMode}
                      invalid={
                        validation.errors.name && validation.touched.name
                          ? true
                          : false
                      }
                    />
                    {validation.errors.name && validation.touched.name ? (
                      <FormFeedback type="invalid">
                        {validation.errors.name}
                      </FormFeedback>
                    ) : null}
                  </div>

                  <div className="mb-3">
                    <Label htmlFor="description-input" className="form-label">
                      Product Description <span className="text-danger">*</span>
                    </Label>
                    <Input
                      type="textarea"
                      className="form-control"
                      id="description-input"
                      name="description"
                      placeholder="Enter product description"
                      rows={4}
                      maxLength={500}
                      value={validation.values.description || ""}
                      onBlur={validation.handleBlur}
                      onChange={validation.handleChange}
                      disabled={isViewMode}
                      invalid={
                        validation.errors.description &&
                        validation.touched.description
                          ? true
                          : false
                      }
                    />
                    <div className="text-muted fs-12 text-end">
                      {(validation.values.description || "").length}/500
                    </div>
                    {validation.errors.description &&
                    validation.touched.description ? (
                      <FormFeedback type="invalid">
                        {validation.errors.description}
                      </FormFeedback>
                    ) : null}
                  </div>

                  {/* Item Group Code and Item Code — hidden from UI (values still
                      submitted from loaded product data). Set to true to restore. */}
                  {false && (
                    <Row>
                      <Col lg={6}>
                        <div className="mb-3">
                          <Label
                            className="form-label"
                            htmlFor="item-group-code-input"
                          >
                            Item Group Code
                          </Label>
                          <Input
                            type="text"
                            className="form-control"
                            id="item-group-code-input"
                            placeholder="Enter item group code"
                            name="item_group_code"
                            value={validation.values.item_group_code || ""}
                            onBlur={validation.handleBlur}
                            onChange={validation.handleChange}
                            invalid={
                              validation.errors.item_group_code &&
                              validation.touched.item_group_code
                                ? true
                                : false
                            }
                          />
                          {validation.errors.item_group_code &&
                          validation.touched.item_group_code ? (
                            <FormFeedback type="invalid">
                              {validation.errors.item_group_code}
                            </FormFeedback>
                          ) : null}
                        </div>
                      </Col>
                      <Col lg={6}>
                        <div className="mb-3">
                          <Label className="form-label" htmlFor="item-code-input">
                            Item Code (Internal)
                          </Label>
                          <Input
                            type="text"
                            className="form-control"
                            id="item-code-input"
                            placeholder="Enter item code"
                            name="item_code"
                            value={validation.values.item_code || ""}
                            onBlur={validation.handleBlur}
                            onChange={validation.handleChange}
                          />
                        </div>
                      </Col>
                    </Row>
                  )}
                </CardBody>
              </Card>

              <Card>
                <CardHeader>
                  <Nav className="nav-tabs-custom card-header-tabs border-bottom-0">
                    <NavItem>
                      <NavLink
                        style={{ cursor: "pointer" }}
                        className={classnames({
                          active: customActiveTab === "1",
                        })}
                        onClick={() => {
                          toggleCustom("1");
                        }}
                      >
                        General Info
                      </NavLink>
                    </NavItem>
                  </Nav>
                </CardHeader>

                <CardBody>
                  <TabContent activeTab={customActiveTab}>
                    <TabPane id="addproduct-general-info" tabId="1">
                      <Row>
                        <Col lg={6}>
                          <div className="mb-3">
                            <label
                              className="form-label"
                              htmlFor="brand-company-input"
                            >
                              Brand Name <span className="text-danger">*</span>
                            </label>
                            <Input
                              type="text"
                              className="form-control"
                              id="brand-company-input"
                              name="brand_company"
                              placeholder="Enter brand name"
                              value={validation.values.brand_company || ""}
                              onBlur={validation.handleBlur}
                              onChange={validation.handleChange}
                              disabled={isViewMode}
                              invalid={
                                validation.errors.brand_company &&
                                validation.touched.brand_company
                                  ? true
                                  : false
                              }
                            />
                            {validation.errors.brand_company &&
                            validation.touched.brand_company ? (
                              <FormFeedback type="invalid">
                                {validation.errors.brand_company}
                              </FormFeedback>
                            ) : null}
                          </div>
                        </Col>
                        <Col lg={6}>
                          <div className="mb-3">
                            <label
                              className="form-label"
                              htmlFor="model-number-input"
                            >
                              Model Number
                            </label>
                            <Input
                              type="text"
                              className="form-control"
                              id="model-number-input"
                              name="model_number"
                              placeholder="Enter model number"
                              value={validation.values.model_number || ""}
                              onBlur={validation.handleBlur}
                              onChange={validation.handleChange}
                              disabled={isViewMode}
                            />
                          </div>
                        </Col>
                      </Row>

                      {/* Product Attributes (key/value pairs) */}
                      <div className="mt-2">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <label className="form-label mb-0">
                            Product Attributes
                          </label>
                          <Button
                            color="soft-primary"
                            size="sm"
                            type="button"
                            onClick={() =>
                              {
                                setProductAttributes((prev) => [
                                  ...prev,
                                  { key: "", value: "" },
                                ]);
                                setShowAllAttributes(true);
                              }
                            }
                            disabled={isViewMode}
                          >
                            <i className="ri-add-line align-middle me-1"></i>
                            Add Attribute
                          </Button>
                        </div>
                        {productAttributes.length === 0 ? (
                          <p className="text-muted fs-13 mb-0">
                            No attributes added. e.g. Size: 2.5mm², Core: 1C,
                            Colour: Red, Voltage: 450/750V
                          </p>
                        ) : (
                          visibleProductAttributes.map((attr, idx) => (
                            <Row key={idx} className="mb-2 align-items-center">
                              <Col lg={5} xs={5}>
                                <Input
                                  type="text"
                                  placeholder="Attribute (e.g. Size)"
                                  value={attr.key}
                                  disabled={isViewMode}
                                  onChange={(e) =>
                                    setProductAttributes((prev) =>
                                      prev.map((a, i) =>
                                        i === idx
                                          ? { ...a, key: e.target.value }
                                          : a
                                      )
                                    )
                                  }
                                />
                              </Col>
                              <Col lg={6} xs={6}>
                                <Input
                                  type="text"
                                  placeholder="Value (e.g. 2.5mm²)"
                                  value={attr.value}
                                  disabled={isViewMode}
                                  onChange={(e) =>
                                    setProductAttributes((prev) =>
                                      prev.map((a, i) =>
                                        i === idx
                                          ? { ...a, value: e.target.value }
                                          : a
                                      )
                                    )
                                  }
                                />
                              </Col>
                              <Col lg={1} xs={1}>
                                <Button
                                  color="soft-danger"
                                  size="sm"
                                  type="button"
                                  onClick={() =>
                                    setProductAttributes((prev) =>
                                      prev.filter((_, i) => i !== idx)
                                    )
                                  }
                                  disabled={isViewMode}
                                >
                                  <i className="ri-delete-bin-line"></i>
                                </Button>
                              </Col>
                            </Row>
                          ))
                        )}
                        {productAttributes.length > DEFAULT_ATTRIBUTE_PREVIEW_LIMIT && (
                          <div className="d-flex align-items-center justify-content-center gap-2 mt-2">
                            <span className="small text-muted">Show</span>
                            {isViewMode ? (
                              <div
                                aria-label="Number of product attributes to show"
                                className="form-control form-control-sm"
                                contentEditable
                                role="spinbutton"
                                suppressContentEditableWarning
                                tabIndex={0}
                                onBlur={(event) => {
                                  setAttributePreviewLimit(
                                    Math.min(
                                      productAttributes.length,
                                      Math.max(1, Number(event.currentTarget.textContent) || 1)
                                    )
                                  );
                                  setShowAllAttributes(false);
                                }}
                                onKeyDown={(event) => {
                                  if (event.key === "Enter") event.currentTarget.blur();
                                }}
                                style={{ width: "60px", textAlign: "center" }}
                              >
                                {attributePreviewLimit}
                              </div>
                            ) : (
                              <Input
                                type="number"
                                min="1"
                                max={productAttributes.length}
                                aria-label="Number of product attributes to show"
                                value={attributePreviewLimit}
                                onChange={(event) => {
                                  setAttributePreviewLimit(
                                    Math.min(
                                      productAttributes.length,
                                      Math.max(1, Number(event.target.value) || 1)
                                    )
                                  );
                                  setShowAllAttributes(false);
                                }}
                                style={{ width: "72px" }}
                              />
                            )}
                            <span className="small text-muted">attributes</span>
                            {isViewMode ? (
                                <div
                                  className="btn btn-soft-primary btn-sm"
                                  role="button"
                                  tabIndex={0}
                                  onClick={toggleAttributePreview}
                                  onKeyDown={(event) => {
                                    if (event.key === "Enter" || event.key === " ") {
                                      event.preventDefault();
                                      toggleAttributePreview();
                                    }
                                  }}
                                >
                                  <i className={`ri-arrow-${isShowingAllAttributes ? "up" : "down"}-s-line align-middle me-1`} />
                                  {isShowingAllAttributes
                                    ? "Show Less"
                                    : `Load More (${productAttributes.length - attributePreviewLimit})`}
                                </div>
                              ) : (
                                <Button
                                  color="soft-primary"
                                  size="sm"
                                  type="button"
                                  onClick={toggleAttributePreview}
                                >
                                  <i className={`ri-arrow-${isShowingAllAttributes ? "up" : "down"}-s-line align-middle me-1`} />
                                  {isShowingAllAttributes
                                    ? "Show Less"
                                    : `Load More (${productAttributes.length - attributePreviewLimit})`}
                                </Button>
                              )}
                          </div>
                        )}
                      </div>
                    </TabPane>
                  </TabContent>
                </CardBody>
              </Card>

              {/* Attachments Table for Edit Mode - Separate Card */}
              {isEditMode && (
                <Card>
                  <CardHeader>
                    <div className="d-flex justify-content-between align-items-center">
                      <h5 className="card-title mb-0">Product Image</h5>
                      {!isViewMode && <Button
                        color="primary"
                        size="sm"
                        onClick={() => setImageSelectionModal(true)}
                        disabled={creating || updating || creatingAttachment}
                      >
                        <i className="ri-image-fill align-middle me-1"></i>
                        Select from Library
                      </Button>}
                    </div>
                  </CardHeader>
                  <CardBody>
                    <div className="table-responsive">
                      <Table className="table-bordered table-nowrap align-middle mb-0">
                        <thead className="table-light">
                          <tr>
                            <th scope="col" style={{ width: "100px" }}>
                              Preview
                            </th>
                            <th scope="col">Name</th>
                            <th scope="col">Description</th>
                            {!isViewMode && <th scope="col" style={{ width: "100px" }}>Action</th>}
                          </tr>
                        </thead>
                        <tbody>
                          {productData?.attachment_urls &&
                          productData.attachment_urls.length > 0 ? (
                            productData.attachment_urls.map(
                              (attachment: any, index: any) => (
                                <tr key={attachment.id}>
                                  <td>
                                    {attachment.file?.match(
                                      /\.(jpeg|jpg|png)$/i
                                    ) ? (
                                      <div
                                        className="avatar-sm bg-light rounded cursor-pointer"
                                        onClick={() =>
                                          handleImagePreview(
                                            attachment.file_url || ""
                                          )
                                        }
                                      >
                                        <img
                                          src={attachment.file_url}
                                          alt={attachment.name}
                                          className="avatar-sm rounded"
                                        />
                                      </div>
                                    ) : (
                                      <div className="avatar-sm bg-light rounded">
                                        <i className="ri-file-text-fill align-middle text-primary fs-16"></i>
                                      </div>
                                    )}
                                  </td>
                                  <td>
                                    <p className="font-weight-bold">
                                      {attachment.name}
                                    </p>
                                  </td>
                                  <td>{attachment.description}</td>
                                  {!isViewMode && <td>
                                    <Button
                                      color="danger"
                                      size="sm"
                                      onClick={() => openDeleteConfirmModal(attachment.id, attachment.name)}
                                      disabled={deletingAttachmentId === attachment.id}
                                    >
                                      {deletingAttachmentId === attachment.id ? (
                                        <Spinner size="sm" />
                                      ) : (
                                        <i className="ri-delete-bin-line"></i>
                                      )}
                                    </Button>
                                  </td>}
                                </tr>
                              )
                            )
                          ) : (
                            <tr>
                              <td colSpan={isViewMode ? 3 : 4} className="text-center py-4">
                                <i className="ri-image-line display-4 text-muted"></i>
                                <p className="mt-2">No images attached to this product</p>
                                {!isViewMode && <p className="text-muted">Click "Select from Library" to add images</p>}
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </Table>
                    </div>
                  </CardBody>
                </Card>
              )}

              {isEditMode && (
                <Card>
                  <CardHeader>
                    <div className="d-flex justify-content-between align-items-center">
                      <h5 className="card-title mb-0">Product Suppliers</h5>
                      {!isViewMode && <Button
                        color="primary"
                        size="sm"
                        onClick={() => setSupplierModal(true)}
                        disabled={creatingSupplierMapping}
                      >
                        <i className="ri-add-line align-middle me-1"></i>
                        Add Suppliers
                      </Button>}
                    </div>
                  </CardHeader>
                  <CardBody>
                    <div className="table-responsive">
                      <Table className="table-bordered table-nowrap align-middle mb-0">
                        <thead className="table-light">
                          <tr>
                            <th scope="col">Supplier Name</th>
                            <th scope="col">Email</th>
                            <th scope="col">Phone</th>
                            <th scope="col">Brand Name</th>
                            <th scope="col">Model Number</th>
                            <th scope="col" className="text-end">
                              Total Amount
                            </th>
                            <th scope="col" className="text-end">
                              Discount (%)
                            </th>
                            {!isViewMode && <th scope="col" style={{ width: "150px" }}>Actions</th>}
                          </tr>
                        </thead>
                        <tbody>
                          {productSuppliers && productSuppliers.length > 0 ? (
                            productSuppliers.map(
                              (supplier: SupplierCostMapping) => (
                                <SupplierRow
                                  key={supplier.mapping_id}
                                  supplier={supplier}
                                  onUpdate={handleUpdateSupplier}
                                  onDelete={handleDeleteSupplier}
                                  isLoading={updatingSupplierMapping}
                                  isDeleting={deletingSupplierMapping}
                                  productId={parseInt(Id as string)}
                                  readOnly={isViewMode}
                                />
                              )
                            )
                          ) : (
                            <tr>
                              <td colSpan={isViewMode ? 7 : 8} className="text-center py-4">
                                No suppliers found.{!isViewMode && " Click 'Add Suppliers' to add suppliers."}
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </Table>
                    </div>
                  </CardBody>
                </Card>
              )}

              {isEditMode && !isViewMode && (
                <AddSupplierModal
                  isOpen={supplierModal}
                  toggle={() => setSupplierModal(!supplierModal)}
                  productId={parseInt(Id as string)}
                  onSubmit={handleAddSupplier}
                  isLoading={creatingSupplierMapping}
                  error={createSupplierError}
                  existingSuppliers={productSuppliers || []}
                />
              )}

              {isEditMode && Id && (
                <ProductDocumentChecklistSection
                  productId={parseInt(Id as string)}
                  readOnly={isViewMode}
                />
              )}

              <div className="text-end mb-3">
                {/* Save / Update button */}
                {!isViewMode && <button
                  type="submit"
                  className="btn btn-success w-sm me-2"
                  disabled={creating || updating}
                >
                  {creating || updating ? (
                    <>
                      <Spinner size="sm" className="me-2" />
                      {isEditMode ? "Updating..." : "Creating..."}
                    </>
                  ) : isEditMode ? (
                    "Update Product"
                  ) : (
                    "Create Product"
                  )}
                </button>}

                {/* Close / Cancel button */}
                <button
                  type="button"
                  className="btn btn-danger w-sm"
                  disabled={creating || updating}
                  onClick={() => history("/inventory")}
                >
                  Close
                </button>
              </div>
            </Form>
          </Col>

          <Col lg={4}>
            <Card>
              <CardHeader>
                <h5 className="card-title mb-0">Publish</h5>
              </CardHeader>
              <CardBody>
                <div className="mb-3">
                  <Label htmlFor="status-input" className="form-label">
                    Status <span className="text-danger">*</span>
                  </Label>
                  <Input
                    name="status"
                    type="select"
                    className="form-select"
                    id="status-input"
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    value={validation.values.status || 1}
                    disabled={creating || updating}
                  >
                    {statusOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Input>
                  {validation.touched.status && validation.errors.status ? (
                    <FormFeedback type="invalid">
                      {validation.errors.status}
                    </FormFeedback>
                  ) : null}
                </div>
              </CardBody>
            </Card>

            <Card>
              <CardHeader>
                <h5 className="card-title mb-0">Product Categories</h5>
              </CardHeader>
              <CardBody>
                <Label htmlFor="category-input" className="form-label">
                  Select product category <span className="text-danger">*</span>
                </Label>
                <Input
                  name="category"
                  type="select"
                  className="form-select"
                  id="category-input"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.category || ""}
                    disabled={creating || updating || isViewMode}
                >
                  <option value="">Select Category</option>
                  {categoryOptions.map((option: any) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Input>
                {validation.touched.category && validation.errors.category ? (
                  <FormFeedback type="invalid">
                    {validation.errors.category}
                  </FormFeedback>
                ) : null}
              </CardBody>
            </Card>

            <Card>
              <CardHeader>
                <h5 className="card-title mb-0">Measurements</h5>
              </CardHeader>
              <CardBody>
                <Label htmlFor="measurement-input" className="form-label">
                  Select measurement <span className="text-danger">*</span>
                </Label>
                <Input
                  name="measurement"
                  type="select"
                  className="form-select"
                  id="measurement-input"
                  onChange={validation.handleChange}
                  onBlur={validation.handleBlur}
                  value={validation.values.measurement || ""}
                    disabled={creating || updating || isViewMode}
                >
                  <option value="">Select Measurement</option>
                  {measurementOptions.map((option: any) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Input>
                {validation.touched.measurement &&
                validation.errors.measurement ? (
                  <FormFeedback type="invalid">
                    {validation.errors.measurement}
                  </FormFeedback>
                ) : null}
              </CardBody>
            </Card>

            <Card>
              <CardHeader>
                <h5 className="card-title mb-0">Product Tags</h5>
              </CardHeader>
              <CardBody>
                <Label htmlFor="tags-input" className="form-label">
                  Select tags
                </Label>
                <Select
                  isMulti
                  value={selectedTags}
                  onChange={handleTagsChange}
                  options={tagOptions}
                  classNamePrefix="select"
                  placeholder="Select tags..."
                  isDisabled={creating || updating || isViewMode}
                />
                {validation.touched.tags && validation.errors.tags ? (
                  <div className="text-danger">{validation.errors.tags}</div>
                ) : null}
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Image Selection Modal */}
        <ProductImageSelectionModal
          isOpen={imageSelectionModal}
          toggle={() => {
            console.log('Closing modal, resetting search');
            // Reset search when closing modal
            setPageState({
              page: 1,
              pageSize: 12,
              search: "",
            });
            setImageSelectionModal(false);
          }}
          onSelect={handleSelectImageFromLibrary}
          selectedImageId={selectedExistingImage?.id}
          isLoading={creatingAttachment}
          // Pass search and pagination props
          currentPage={page.page}
          pageSize={page.pageSize}
          searchTerm={page.search}
          onPageChange={handlePageChange}
          onSearchChange={handleSearchChange}
          onClearSearch={handleClearSearch}
        />

        {/* Confirmation Modal for Image Selection */}
        <Modal
          isOpen={imageConfirmModal.isOpen}
          toggle={() => setImageConfirmModal({ isOpen: false, image: null })}
        >
          <ModalHeader toggle={() => setImageConfirmModal({ isOpen: false, image: null })}>
            Confirm Image Selection
          </ModalHeader>
          <ModalBody>
            <div className="text-center mb-3">
              <img
                src={imageConfirmModal.image?.file_url}
                alt={imageConfirmModal.image?.name}
                className="img-fluid rounded mb-3"
                style={{ maxHeight: "200px", objectFit: "contain" }}
              />
              <h6 className="mb-2">{imageConfirmModal.image?.name}</h6>
              <p>
                Are you sure you want to attach this image to your product?
              </p>
              {imageConfirmModal.image?.products && imageConfirmModal.image.products.length > 0 && (
                <div className="alert alert-info mt-2">
                  <i className="ri-information-line me-1"></i>
                  This image is already used in {imageConfirmModal.image.products.length} other product(s)
                </div>
              )}
            </div>
          </ModalBody>
          <ModalFooter>
            <Button
              color="secondary"
              onClick={() => setImageConfirmModal({ isOpen: false, image: null })}
              disabled={updatingImage}
            >
              Cancel
            </Button>
            <Button
              color="primary"
              onClick={handleConfirmImageSelection}
              disabled={updatingImage}
            >
              {updatingImage ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Attaching...
                </>
              ) : (
                <>
                  <i className="ri-check-line me-1"></i>
                  Attach Image
                </>
              )}
            </Button>
          </ModalFooter>
        </Modal>

        {/* Delete Confirmation Modal */}
        <Modal
          isOpen={deleteConfirmModal.isOpen}
          toggle={() => setDeleteConfirmModal({
            isOpen: false,
            attachmentId: null,
            attachmentName: "",
          })}
        >
          <ModalHeader toggle={() => setDeleteConfirmModal({
            isOpen: false,
            attachmentId: null,
            attachmentName: "",
          })}>
            Confirm Delete
          </ModalHeader>
          <ModalBody>
            <p>
              Are you sure you want to remove "<strong>{deleteConfirmModal.attachmentName}</strong>" from this product?
            </p>
            <p className="text-muted">
              This action cannot be undone. The image will only be removed from this product, not from the library.
            </p>
          </ModalBody>
          <ModalFooter>
            <Button
              color="secondary"
              onClick={() => setDeleteConfirmModal({
                isOpen: false,
                attachmentId: null,
                attachmentName: "",
              })}
              disabled={deletingAttachmentId === deleteConfirmModal.attachmentId}
            >
              Cancel
            </Button>
            <Button
              color="danger"
              onClick={() => {
                if (deleteConfirmModal.attachmentId) {
                  handleRemoveExistingFile(deleteConfirmModal.attachmentId);
                }
              }}
              disabled={deletingAttachmentId === deleteConfirmModal.attachmentId}
            >
              {deletingAttachmentId === deleteConfirmModal.attachmentId ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Deleting...
                </>
              ) : (
                "Delete Image"
              )}
            </Button>
          </ModalFooter>
        </Modal>

        {/* Add Attachment Modal */}
        <Modal
          isOpen={attachmentModal}
          toggle={() => setAttachmentModal(!attachmentModal)}
        >
          <ModalHeader toggle={() => setAttachmentModal(!attachmentModal)}>
            Add New Attachment
          </ModalHeader>
          <ModalBody>
            <div className="mb-3">
              <Label htmlFor="attachment-name" className="form-label">
                Attachment Name
              </Label>
              <Input
                type="text"
                className="form-control"
                id="attachment-name"
                placeholder="Enter attachment name"
                value={newAttachment.name}
                onChange={(e) =>
                  setNewAttachment({ ...newAttachment, name: e.target.value })
                }
                disabled={creatingAttachment}
              />
            </div>
            <div className="mb-3">
              <Label htmlFor="attachment-description" className="form-label">
                Description
              </Label>
              <Input
                type="textarea"
                className="form-control"
                id="attachment-description"
                placeholder="Enter description"
                value={newAttachment.description}
                onChange={(e) =>
                  setNewAttachment({
                    ...newAttachment,
                    description: e.target.value,
                  })
                }
                disabled={creatingAttachment}
              />
            </div>
            <div className="mb-3">
              <Label htmlFor="attachment-file" className="form-label">
                File <span className="text-danger">*</span>
              </Label>
              <Input
                type="file"
                className="form-control"
                id="attachment-file"
                onChange={(e) =>
                  setNewAttachment({
                    ...newAttachment,
                    file: e.target.files?.[0] || null,
                  })
                }
                disabled={creatingAttachment}
              />
            </div>
          </ModalBody>
          <ModalFooter>
            <Button
              color="secondary"
              onClick={() => setAttachmentModal(false)}
              disabled={creatingAttachment}
            >
              Cancel
            </Button>
            <Button
              color="primary"
              onClick={handleAddAttachment}
              disabled={!newAttachment.file || creatingAttachment}
            >
              {creatingAttachment ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Adding...
                </>
              ) : (
                "Add Attachment"
              )}
            </Button>
          </ModalFooter>
        </Modal>

        {/* Image Preview Modal */}
        <Modal
          isOpen={imagePreviewModal}
          toggle={() => setImagePreviewModal(!imagePreviewModal)}
          size="lg"
        >
          <ModalHeader toggle={() => setImagePreviewModal(!imagePreviewModal)}>
            Image Preview
          </ModalHeader>
          <ModalBody className="text-center">
            <img src={previewImage} alt="Preview" className="img-fluid" />
          </ModalBody>
          <ModalFooter>
            <Button
              color="secondary"
              onClick={() => setImagePreviewModal(false)}
            >
              Close
            </Button>
          </ModalFooter>
        </Modal>
        <ToastContainer />
      </Container>
    </div>
  );
};

export default EcommerceAddProduct;
